import { useFieldArray, useFormContext } from 'react-hook-form';
import FormGridItem from '~/components/FormComponents/FormGridItem.tsx';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import {
  accountTableColumns,
  controllingPersonStatusMap,
  EMPTY_ORGANIZATIONS,
} from '~/modules/identification/forms/TaxResidencyAdditionalInformation/constants.ts';
import { Box, Grid, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { AddRemoveBlock } from '~/components/AddRemoveBlock/AddRemoveBlock.tsx';
import { TOrganizations } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/types.ts';
import { styles as styles } from '~/modules/identification/forms/AdditionalInformation/styles.ts';
import { styles as titleStyles } from '~/modules/identification/styles.ts';
import { TableHeader } from '~/components/ListCardTable/TableHeader.tsx';
import { ACCOUNT } from '~/modules/identification/forms/AdditionalInformation/constants.ts';
import { commonStyles } from '~/theme/styles/commonStyles.ts';
import { AccountTable } from '~/modules/identification/forms/AdditionalInformation/TaxResident/AccountTable.tsx';
import { theme } from '~/theme';
import { useSelector } from 'react-redux';
import { selectGeneralInfoFullNameUkr } from '~/store/identification-form/selectors.ts';
import { useEffect } from 'react';
import { InfoTooltip } from '~/components/InfoTooltip/InfoTooltip.tsx';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import { styles as stylesInfoIcon } from '~/components/FormComponents/styles.ts';
import { infoMessages } from '~/constants/infoMessages';

interface IOrganizationsProps {
  index: number;
  append: (item: TOrganizations) => void;
  remove: (index: number) => void;
  section?: string;
}

export const Organizations = ({ append, remove, index, section }: IOrganizationsProps) => {
  const { control, setValue } = useFormContext();
  const fullNameUkr = useSelector(selectGeneralInfoFullNameUkr) || {};
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const basePath = section ? `${section}.organizations.${index}` : `organizations.${index}`;

  const {
    fields: accountFields,
    append: appendAccount,
    remove: removeAccount,
  } = useFieldArray({
    control,
    name: `${basePath}.accounts`,
  });

  if (!accountFields.length) appendAccount(ACCOUNT);

  useEffect(() => {
    if (fullNameUkr) {
      if (index === 0) setValue(`${basePath}.name`, fullNameUkr);
    }
  }, [fullNameUkr]);

  return (
    <>
      <FormGridItem>
        <FormInput
          name={`${basePath}.name`}
          label={`Повне найменування організації (Організація ${index + 1})`}
          placeholder="Введіть повне найменування організації"
          disabled={index === 0 ? true : false}
          fullWidth
        />
      </FormGridItem>
      <FormGridItem>
        <FormAutocomplete
          name={`${basePath}.controllingPersonStatus`}
          options={controllingPersonStatusMap}
          inputProps={{ placeholder: 'Оберіть статус в організації' }}
          label={
            <Box sx={{ ...commonStyles.centerFlexContainer, gap: 1 }}>
              <Typography variant="body2">Статус Контролюючої Особи</Typography>
              <InfoTooltip
                title={
                  'Вкажіть статус Контролюючої Особи (КБВ), обравши відповідну опцію із списку'
                }
              />
            </Box>
          }
          fullWidth
        />
      </FormGridItem>
      <Box sx={{ width: '100%' }}>
        <Typography variant="h6" sx={{ ...titleStyles.titleForm, padding: 0 }}>
          {`Номер рахунку організації ${index + 1} `}
          <Tooltip title={infoMessages.IBAN_INFORMER} placement="right">
            <InfoOutlineIcon sx={stylesInfoIcon.infoIcon} />
          </Tooltip>
        </Typography>
        <Box sx={styles.table}>
          {!isMobile && <TableHeader columns={accountTableColumns} showActions={false} />}
          <Box sx={{ ...commonStyles.columnFlexContainer, gap: { sm: 2, md: 0, xs: 2 } }}>
            {accountFields.map((account, accountIndex: number) => (
              <AccountTable
                key={account.id}
                section={basePath}
                index={accountIndex}
                disabled={false}
                shownTableAction
                append={appendAccount}
                remove={removeAccount}
              />
            ))}
          </Box>
        </Box>
      </Box>
      <Grid size={12}>
        <AddRemoveBlock
          add={() => append(EMPTY_ORGANIZATIONS)}
          remove={() => remove(index)}
          isPlusShown
          isMinusShown={index === 0 ? false : true}
          plusText='Натисніть на "+" для додавання організації'
          minusText='Натисніть на "-" для видалення організації'
        />
      </Grid>
    </>
  );
};
